import React, { useCallback, useRef, useState } from 'react';
import { Row, Col, Button } from 'reactstrap';

import {
  AudioPlayer, Intl, Select,
} from 'components';
import bg from 'assets/img/step3Bg.png';
import { connect } from 'react-redux';
import { handlePromise } from 'utils';
import CampaignService from 'api/Campaign';
import SettingsActions from 'store/reducers/Settings';
import UploadModal from '../utils/UploadFile/Modal';
import FavouriteModal from '../utils/AudioFavourite/Modal';
import SurveyAudios from '../utils/SurveyAudios';
import AudioContainer from '../utils/AudioContainer';
import SaveFavouriteModal from '../utils/SaveFavouriteModal';

const Step3 = ( {
  campaignId, form, values, onOpenChangeModal, onComplete, onOpenTtsModal,
  onOpenIvrModal, toggleLoading,
} ) => {
  const [uploadModalOpened, setUploadModalOpened] = useState( false );
  const [favouriteModalOpened, setFavouriteModalOpened] = useState( false );
  const [saveFavouriteModalOpened, setSaveFavouriteModalOpened] = useState( false );
  const favAudioFieldRef = useRef( null );

  const toggleModal = useCallback( () => {
    if ( values.audioSource === 'localFile' ) {
      setUploadModalOpened( !uploadModalOpened );
    }
    if ( values.audioSource === 'favourites' ) {
      setFavouriteModalOpened( !favouriteModalOpened );
    }
  }, [uploadModalOpened, favouriteModalOpened, values] );

  const toggleSaveFavouriteModal = useCallback( ( audioField ) => {
    favAudioFieldRef.current = audioField;
    setSaveFavouriteModalOpened( !saveFavouriteModalOpened );
  }, [saveFavouriteModalOpened] );

  const setFileValues = useCallback( ( fileValues ) => {
    if ( values.audioSource === 'localFile' || values.audioSource === 'favourites' ) {
      if ( values.messageType === 'simple_message' ) {
        form.change( 'singleFile', fileValues.singleFile );
        onComplete( [{ step: 2, completed: true }] );
      } else {
        form.change( 'introductionFile', fileValues.introductionFile );
        form.change( 'optionsFile', fileValues.optionsFile );
        form.change( 'farewellFile', fileValues.farewellFile );
        onComplete( [{ step: 2, completed: true }] );
      }
    }
    toggleModal();
  }, [form, values.audioSource, values.messageType, toggleModal, onComplete] );

  const saveFavourite = useCallback( async ( dataToSend ) => {
    toggleLoading( true );
    const [errors, response] = await handlePromise(
      CampaignService.saveFavouriteAudio( campaignId, {
        id: values[favAudioFieldRef.current].id,
        originalName: dataToSend.originalName,
      } ),
    );
    toggleLoading( false );
    if ( !response.ok ) return errors;
    form.change( favAudioFieldRef.current, {
      ...values[favAudioFieldRef.current],
      originalName: dataToSend.originalName,
    } );
    toggleSaveFavouriteModal();
  }, [campaignId, form, values, toggleSaveFavouriteModal, toggleLoading] );

  return (
    <>
      <Row className="campaign-wizard-step-3">
        <Col md className="d-flex flex-column justify-content-center">
          <img
            src={bg}
            alt="..."
            className="campaign-wizard-triangle img-fluid d-none d-md-block"
            style={{ minHeight: 180 }}
          />

          <Row noGutters className="w-100">
            <Col md={10} xs={12} className="m-auto">
              <Select
                field="messageType"
                placeholder="campaignWizardMessageType"
                translateOptions
                options={[
                  { id: 'poll', name: 'survey' },
                  { id: 'simple_message', name: 'simpleMessage' },
                  { id: 'ivr', name: 'advancedInteraction' },
                ]}
                onChange={( val, prevValue ) => {
                  if ( val === 'ivr' ) {
                    onOpenIvrModal();
                    form.change( 'messageType', prevValue );
                  } else {
                    onOpenChangeModal( () => form.change( 'messageType', prevValue ) );
                  }
                }}
              />
              <Select
                field="audioSource"
                placeholder="campaignWizardAudioSource"
                translateOptions
                options={[
                  { id: 'tts', name: 'tts' },
                  { id: 'favourites', name: 'favourites' },
                  { id: 'localFile', name: 'localFile' },
                ]}
                onChange={( val, prevValue ) => {
                  if ( val === 'tts' ) {
                    onOpenTtsModal();
                    form.change( 'audioSource', prevValue );
                  } else {
                    onOpenChangeModal( () => form.change( 'audioSource', prevValue ) );
                  }
                }}
              />
              <Button
                color="app-default"
                size="app-sm"
                block
                disabled={!values.messageType || !values.audioSource}
                onClick={toggleModal}
              >
                <Intl id="setAudioBtn" />
              </Button>
            </Col>
          </Row>
        </Col>

        <Col md className="d-flex flex-column justify-content-center mt-4 mt-md-0">
          {values.messageType === 'simple_message' && values.singleFile
          && (
            <AudioContainer onSaveFavourite={() => toggleSaveFavouriteModal( 'singleFile' )} id={4}>
              <AudioPlayer src={values.singleFile.uri} />
            </AudioContainer>
          )}

          {values.messageType === 'poll' && <SurveyAudios values={values} onSaveFavourite={toggleSaveFavouriteModal} />}
        </Col>
      </Row>

      {values.audioSource === 'localFile' && uploadModalOpened
      && (
        <UploadModal
          campaignId={campaignId}
          values={values}
          open={uploadModalOpened}
          onClose={toggleModal}
          onSubmit={setFileValues}
        />
      )}
      {values.audioSource === 'favourites' && favouriteModalOpened
      && (
        <FavouriteModal
          values={values}
          open={favouriteModalOpened}
          onClose={toggleModal}
          onSubmit={setFileValues}
        />
      )}

      {saveFavouriteModalOpened
      && (
        <SaveFavouriteModal
          values={{
            originalName: values[favAudioFieldRef.current]
            && values[favAudioFieldRef.current].userDefineName
              ? values[favAudioFieldRef.current].userDefineName.replace( /.mp3/g, '' ) : values[favAudioFieldRef.current]
              && values[favAudioFieldRef.current].originalName
                ? values[favAudioFieldRef.current].originalName.replace( /.mp3/g, '' ) : null,
          }}
          open={saveFavouriteModalOpened}
          onClose={toggleSaveFavouriteModal}
          onSubmit={saveFavourite}
        />
      )}
    </>
  );
};

const mapDispatchToProps = ( {
  toggleLoading: SettingsActions.toggleLoading,
} );

export default connect( null, mapDispatchToProps )( Step3 );
